/**
 * Timezone helpers for rodeo + jackpot wall-clock fields (entry close,
 * performance start). The bug they fix: `<input type="datetime-local">`
 * produces naive `YYYY-MM-DDTHH:mm` strings. Shipping that verbatim into a
 * `timestamptz` column lets PostgreSQL interpret the wall-clock in whatever
 * session TZ it happens to be in, and viewers in a different TZ then see
 * the time shift (the user's "saved 7pm, displays 1pm" report).
 *
 * The fix: anchor all wall-clock inputs to the rodeo's saved `timezone`
 * before converting to a UTC ISO string for storage, and convert back the
 * same way on read. Display formatters always pin the rendered TZ to the
 * rodeo's saved value so the time the admin entered is the time every
 * viewer sees (with the abbreviation as a hint).
 *
 * The wizard restricts the user-facing choice to four US zones; the column
 * is plain TEXT so any IANA value still round-trips.
 */
export const US_TIMEZONES = [
  { value: 'America/Los_Angeles', abbr: 'PT', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', abbr: 'MT', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', abbr: 'CT', label: 'Central Time (CT)' },
  { value: 'America/New_York', abbr: 'ET', label: 'Eastern Time (ET)' },
] as const;

export type UsTimezone = (typeof US_TIMEZONES)[number]['value'];

const ABBR_BY_TZ: Record<string, string> = US_TIMEZONES.reduce(
  (acc, tz) => {
    acc[tz.value] = tz.abbr;
    return acc;
  },
  {} as Record<string, string>,
);

const DEFAULT_TIMEZONE: UsTimezone = 'America/Los_Angeles';

/**
 * Best-effort browser → US-zone mapping. Anything outside the four US zones
 * the wizard offers falls back to Pacific so a non-US admin still gets a
 * sane default they can change.
 */
export function getDefaultTimezone(): UsTimezone {
  let resolved: string;
  try {
    resolved = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return DEFAULT_TIMEZONE;
  }
  if (US_TIMEZONES.some((t) => t.value === resolved)) {
    return resolved as UsTimezone;
  }
  // Common aliases the runtime sometimes returns.
  const aliasMap: Record<string, UsTimezone> = {
    'US/Pacific': 'America/Los_Angeles',
    'US/Mountain': 'America/Denver',
    'US/Arizona': 'America/Denver',
    'US/Central': 'America/Chicago',
    'US/Eastern': 'America/New_York',
    'America/Phoenix': 'America/Denver',
    'America/Boise': 'America/Denver',
    'America/Detroit': 'America/New_York',
    'America/Indiana/Indianapolis': 'America/New_York',
    'America/Kentucky/Louisville': 'America/New_York',
  };
  return aliasMap[resolved] ?? DEFAULT_TIMEZONE;
}

export function getTimezoneAbbreviation(timezone: string | null | undefined): string {
  if (!timezone) return '';
  return ABBR_BY_TZ[timezone] ?? '';
}

/**
 * The current calendar date in `timezone`, formatted as `YYYY-MM-DD`. `en-CA`
 * yields ISO-ordered parts that sort lexicographically the same way they sort
 * chronologically, so callers can string-compare. Falls back to the host
 * timezone if the IANA id is unrecognised.
 */
export function todayInTimezone(timezone: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  }
}

/**
 * True once a rodeo/jackpot's last day is strictly in the past relative to
 * "today" in the rodeo's OWN timezone — i.e. we're at least one calendar day
 * after its final running day.
 *
 * This fixes the upcoming/past bug where a UTC-based comparison
 * (`new Date().toISOString()`) flipped an evening Pacific rodeo into "Past"
 * the moment local time passed 5pm (midnight UTC). A rodeo whose last day is
 * today stays upcoming through 11:59pm in its timezone and only becomes past
 * at the following midnight there.
 *
 * The last day is `endDate ?? startDate`; an item with neither date is never
 * past (in-setup drafts stay visible). Date inputs may be `YYYY-MM-DD` DATE
 * strings or full ISO timestamps — only the date portion is compared. Mirrors
 * the backend `rodeoHasFinished` in
 * api_backend_fastify_server/src/utils/rodeo-date.ts so the list and the
 * server agree on when a rodeo rolls into the past.
 */
export function isScheduleItemPast(opts: {
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  timezone?: string | null;
  now?: Date;
}): boolean {
  const last = (opts.endDate ?? opts.startDate)?.trim();
  if (!last) return false;

  const lastDay = last.slice(0, 10);
  if (lastDay.length !== 10) return false;

  const timezone = opts.timezone?.trim() || DEFAULT_TIMEZONE;
  const today = todayInTimezone(timezone, opts.now ?? new Date());

  // Strict comparison: past only the day AFTER the last running day.
  return today > lastDay;
}

/**
 * Returns the offset (in milliseconds) between UTC and `timezone` at the
 * given moment. Positive when the zone is east of UTC. Used by the
 * conversion helpers below; not part of the public API.
 */
function getTimezoneOffsetMs(date: Date, timezone: string): number {
  // Render the same instant once formatted as if it were UTC and once
  // formatted in the target zone — the gap between the two `Date`s is the
  // zone's offset for that moment (handles DST automatically).
  const utcParts = formatToParts(date, 'UTC');
  const tzParts = formatToParts(date, timezone);
  return Date.UTC(
    tzParts.year, tzParts.month - 1, tzParts.day,
    tzParts.hour, tzParts.minute, tzParts.second,
  ) - Date.UTC(
    utcParts.year, utcParts.month - 1, utcParts.day,
    utcParts.hour, utcParts.minute, utcParts.second,
  );
}

interface DateParts {
  year: number; month: number; day: number;
  hour: number; minute: number; second: number;
}

function formatToParts(date: Date, timezone: string): DateParts {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const result: DateParts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const part of fmt.formatToParts(date)) {
    if (part.type === 'year') result.year = Number(part.value);
    else if (part.type === 'month') result.month = Number(part.value);
    else if (part.type === 'day') result.day = Number(part.value);
    else if (part.type === 'hour') {
      // Intl returns "24" at midnight in some locales; clamp to 0.
      const h = Number(part.value);
      result.hour = h === 24 ? 0 : h;
    }
    else if (part.type === 'minute') result.minute = Number(part.value);
    else if (part.type === 'second') result.second = Number(part.value);
  }
  return result;
}

/**
 * Convert a wall-clock `<input type="datetime-local">` string ("YYYY-MM-DDTHH:mm")
 * interpreted in `timezone` to a UTC ISO timestamp suitable for a timestamptz
 * column. Returns `null` when the input is empty/unparseable so callers can
 * skip the field on save instead of writing garbage.
 *
 * DST note: the wall-clock during a spring-forward gap (e.g. 2:30 AM on the
 * day clocks jump from 2 → 3 in the US) doesn't exist; we emit the offset of
 * the *post-jump* zone, matching how `<input type="datetime-local">` rounds.
 */
export function localInputToUtcIso(
  naive: string | null | undefined,
  timezone: string,
): string | null {
  if (!naive) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(naive);
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  // First pass: pretend the wall-clock is UTC so we can probe the offset.
  const guessUtc = Date.UTC(
    Number(y), Number(mo) - 1, Number(d),
    Number(h), Number(mi), Number(s ?? '0'),
  );
  const offset = getTimezoneOffsetMs(new Date(guessUtc), timezone);
  // Real UTC = wall-clock interpreted as the zone, minus the zone's offset.
  return new Date(guessUtc - offset).toISOString();
}

/**
 * Reverse of `localInputToUtcIso`: take a UTC ISO timestamp and return what
 * `<input type="datetime-local">` should display in `timezone`. Empty/invalid
 * inputs collapse to `''` so the input renders blank rather than throwing.
 */
export function utcIsoToLocalInput(
  iso: string | null | undefined,
  timezone: string,
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = formatToParts(d, timezone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

/**
 * Split a UTC ISO timestamp into the wall-clock `YYYY-MM-DD` and `HH:mm:ss`
 * pieces that the rodeo wizard's performance step still ships back to the
 * server as separate fields. Both components are pinned to `timezone` so a
 * 7pm Pacific performance reads as `19:00:00` regardless of viewer locale.
 */
export function utcIsoToDateAndTime(
  iso: string | null | undefined,
  timezone: string,
): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const parts = formatToParts(d, timezone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`,
    time: `${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`,
  };
}

/**
 * Format a UTC ISO timestamp in the rodeo's saved timezone, optionally with
 * the abbreviation appended ("Apr 5, 2026, 7:00 PM PT"). Pin the rendered
 * zone explicitly so the same row reads identically for every admin
 * regardless of the browser they're sitting at.
 */
export function formatInTimezone(
  iso: string | null | undefined,
  timezone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
  appendAbbreviation = true,
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const formatted = d.toLocaleString(undefined, { ...options, timeZone: timezone });
  if (!appendAbbreviation) return formatted;
  const abbr = getTimezoneAbbreviation(timezone);
  return abbr ? `${formatted} ${abbr}` : formatted;
}

/**
 * `Intl`-formatted time-only string ("7:00 PM PT") used on table rows where
 * the date is already shown elsewhere in the row.
 */
export function formatTimeInTimezone(
  iso: string | null | undefined,
  timezone: string,
  appendAbbreviation = true,
): string {
  return formatInTimezone(
    iso,
    timezone,
    { hour: 'numeric', minute: '2-digit' },
    appendAbbreviation,
  );
}
